import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Copy, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/hv/Seo";
import { FadeIn } from "@/components/hv/Motion";
import { LogoMark } from "@/components/hv/Logo";

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n).toFixed(2)}`;

const Stat = ({ label, value, sub, testId }) => (
  <div className="rounded-2xl border border-[#E8DDD3] bg-white p-5" data-testid={testId}>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">{label}</div>
    <div className="mt-2 font-serif text-3xl tracking-tight text-[#1F1A17]">{value}</div>
    {sub && <div className="mt-1 text-xs text-[#685F58]">{sub}</div>}
  </div>
);

const CopyLink = ({ label, url, testId }) => {
  const [done, setDone] = useState(false);
  const copy = async () => { await navigator.clipboard.writeText(url); setDone(true); toast.success("Link copied"); setTimeout(() => setDone(false), 1500); };
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#E8DDD3] bg-white px-4 py-3">
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">{label}</div>
        <div className="truncate text-xs text-[#1F1A17]" data-testid={`${testId}-url`}>{url}</div>
      </div>
      <button onClick={copy} data-testid={testId} className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] h-9 px-3.5 text-xs text-[#FBF8F4] transition-colors">{done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy</button>
    </div>
  );
};

export default function PartnerPortalPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    fetch(`${API}/api/partners/portal/${token}`).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail); return r.json(); }).then(setD).catch((e) => setErr(e.message));
  }, [token]);

  if (err) return (
    <div className="max-w-md mx-auto px-5 py-28 text-center" data-testid="portal-error">
      <LogoMark className="mx-auto h-8 w-8 text-[#B4532A]" />
      <h1 className="mt-6 font-serif text-3xl tracking-tight text-[#1F1A17]">{err}</h1>
      <p className="mt-3 text-sm text-[#685F58]">Check the link in your welcome email, or reply to it and we'll send a fresh one.</p>
    </div>
  );
  if (!d) return <div className="py-40 text-center text-sm text-[#685F58]" data-testid="portal-loading">Loading your numbers…</div>;

  const a = d.allTime;
  const bp = d.bonusProgress;
  return (
    <div data-testid="portal-page">
      <Seo title={`${d.firstName}'s HearthVault partner dashboard`} description="Private partner dashboard." noindex />
      <FadeIn className="max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#B4532A]">HearthVault partner · {d.code}</div>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight text-[#1F1A17]" data-testid="portal-title">Hello, {d.firstName}.</h1>
            <p className="mt-3 text-sm text-[#685F58]">Your private dashboard. Partner since {d.since}{d.status === "paused" ? " · currently paused" : ""}. Bookmark this page — the link is your login.</p>
          </div>
          <div className="rounded-2xl bg-[#181412] text-[#FBF8F4] px-6 py-5" data-testid="portal-balance">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4A373]">Outstanding balance</div>
            <div className="mt-1 font-serif text-4xl">{money(a.balance)}</div>
            <div className="text-xs text-[#C8BFB8]">Earned {money(a.earned)} · paid {money(a.paid)}</div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Clicks" value={a.clicks} sub={`${a.signup_clicks} went on to sign-up`} testId="portal-clicks" />
          <Stat label="Signups" value={a.signups} testId="portal-signups" />
          <Stat label="Active families" value={a.activated} sub={`${Math.max(0, bp.threshold - bp.activated)} to the $${bp.amount} bonus`} testId="portal-active" />
          <Stat label="Referred revenue" value={money(a.revenue)} sub={`${d.terms.commissionPct}% commission → ${money(a.commission)}`} testId="portal-revenue" />
        </div>

        <div className="mt-4 rounded-2xl border border-[#E8DDD3] bg-white p-5" data-testid="portal-bonus">
          <div className="flex items-center justify-between text-xs text-[#685F58]"><span>Bonus progress</span><span>{bp.activated} / {bp.threshold} active families</span></div>
          <div className="mt-2 h-2 rounded-full bg-[#F3ECE5] overflow-hidden"><div className="h-full rounded-full bg-[#B4532A]" style={{ width: `${Math.min(100, (bp.activated / bp.threshold) * 100)}%` }} /></div>
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-4">
          {d.landingLink && <CopyLink label="Your landing link (best for posts)" url={d.landingLink} testId="portal-copy-landing" />}
          <CopyLink label="Direct signup link" url={d.directLink} testId="portal-copy-direct" />
        </div>

        <div className="mt-10 grid lg:grid-cols-[1.4fr_1fr] gap-6">
          <div className="rounded-[2rem] border border-[#E8DDD3] bg-white p-6 sm:p-8" data-testid="portal-months">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">Month by month</div>
            <table className="mt-4 w-full text-sm">
              <thead><tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]"><th className="text-left py-2 font-normal">Month</th><th className="text-right py-2 font-normal">Clicks</th><th className="text-right py-2 font-normal">Signups</th><th className="text-right py-2 font-normal">Active</th><th className="text-right py-2 font-normal">Commission</th></tr></thead>
              <tbody>
                {d.months.map((m) => (
                  <tr key={m.month} className="border-t border-[#E8DDD3]">
                    <td className="py-3 text-[#1F1A17]">{m.month}</td><td className="py-3 text-right">{m.clicks}</td><td className="py-3 text-right">{m.signups}</td><td className="py-3 text-right">{m.activated}</td><td className="py-3 text-right font-medium text-[#1F1A17]">{money(m.commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-[2rem] border border-[#E8DDD3] bg-white p-6 sm:p-8" data-testid="portal-payouts">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">Payouts</div>
            {d.payouts.length === 0 ? <p className="mt-4 text-sm text-[#685F58]">No payouts yet. Statements go out on the 1st; payment follows within 10 days.</p> : (
              <ul className="mt-4 divide-y divide-[#E8DDD3]">
                {d.payouts.map((x) => <li key={x.id} className="flex items-center justify-between py-3 text-sm"><span className="text-[#685F58]">{x.period || x.paidAt.slice(0, 10)}</span><span className="font-medium text-[#1F1A17]">{money(x.amount)}</span></li>)}
              </ul>
            )}
            <p className="mt-4 text-xs text-[#685F58]">Last statement: {d.statements.last ? d.statements.last.slice(0, 10) : "none yet"}. Terms: {d.terms.commissionPct}% for {d.terms.commissionMonths} months · ${d.terms.bonusAmount} at {d.terms.bonusThreshold} active families.</p>
          </div>
        </div>

        <p className="mt-10 flex items-center gap-2 text-xs text-[#685F58]"><Link2 className="h-3.5 w-3.5" /> Questions or a change of payout details — reply to any HearthVault email and a person will answer.</p>
      </FadeIn>
    </div>
  );
}
